<script setup lang="ts">
/**
 * 数据范围受限横幅（1.1.85）
 * ─────────────────────────────────────────────────────────────
 * 为什么单独做一个组件、且由 AppShell 在「核心决策页」顶部统一渲染：
 *   评估发现（权限评估文档）后端按 SPU 数据范围**静默**删行（data-scope.ts），
 *   之前只有仪表盘 / 工厂可出货页有提示，而真正用来做决策的
 *   备货计划 / 在库 / 在途 / 库龄 / 销量 / 工厂订单 这些页面**没有任何提示**，
 *   受限账号（部门管理员）会"以为自己看的是全量"去备货，漏掉一部分货而不自知。
 *   所以这里常驻显示、不随点击消失（没有关闭按钮），让用户随时能看到自己被收窄了。
 *
 * 三种情况（语义与后端 data_scope.spus 对齐）：
 *   · spus === null  → 不受限（管理员及以上）：**完全不显示**（不该看到"你被授权了无限个"这种废话）
 *   · spus === []    → 一个 SPU 都没配：显示**严重警告**（列表会是空的）
 *   · 非空数组        → 受限：显示"你只看到被授权的 N 个 / 全库共 M 个"
 */
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';

const auth = useAuthStore();

const scope = computed(() => auth.user?.access?.data_scope ?? null);
const spus = computed(() => scope.value?.spus ?? null); // null=不受限, []=空, 数组=受限
const total = computed(() => scope.value?.spuTotal ?? null);

type Mode = 'none' | 'empty' | 'limited';
const mode = computed<Mode>(() => {
  const s = spus.value;
  if (s === null) return 'none';
  if (s.length === 0) return 'empty';
  return 'limited';
});

// null（不受限）时不渲染任何东西
const visible = computed(() => mode.value !== 'none');
const count = computed(() => spus.value?.length ?? 0);
</script>

<template>
  <div
    v-if="visible"
    class="scope-notice flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
    :class="mode === 'empty' ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-sky-300 bg-sky-50 text-sky-900'"
    role="status"
  >
    <span class="mt-0.5 shrink-0 text-base leading-none" aria-hidden="true">{{ mode === 'empty' ? '⚠' : 'ⓘ' }}</span>
    <div class="min-w-0 space-y-1">
      <p v-if="mode === 'empty'" class="font-semibold">
        你当前没有任何 SPU 数据权限，列表会是空的
      </p>
      <p v-else class="font-semibold">
        你当前只看到被授权的 {{ count }} 个 SPU，全库共 {{ total == null ? '—' : total }} 个
      </p>
      <p class="text-[13px] opacity-90">
        <template v-if="mode === 'empty'">
          未授权的 SPU 不会出现在任何列表 / 统计里。请联系管理员在「权限管理 → 人员授权 / SPU 数据权限」里给你分配 SPU。
        </template>
        <template v-else>
          未授权的 SPU 不会出现在列表和统计里，也不会计入合计。
          <a
            class="font-medium underline underline-offset-2 hover:opacity-80"
            href="#"
            title="请联系管理员：在「权限管理 → 人员授权 / SPU 数据权限」里把需要的 SPU 授给你"
            @click.prevent
            >申请更多权限 →</a
          >
        </template>
      </p>
    </div>
  </div>
</template>
